// src/App.jsx
import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, MapPin, Facebook, Instagram, Search, ChevronLeft, ChevronRight, Loader, Sun, Moon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import logo from './images/logo.png';
import banner from './images/banner.png';
import banner1 from './images/banner1.png';
import banner2 from './images/banner2.png';
import medicamentos from './images/medica1.png';
import insumos from './images/dispo1.png';
import dispositivos from './images/equipo1.png';
import personal from './images/personal_1.png';
import genericos from './images/genericos.webp';
import comerciales from './images/comerciales.jpg';
import altocosto from './images/altocosto.jpg';
import fabotera from './images/faboteraputicos.jpg';
import equipo1 from './images/equipo1.jpg';
import equipo2 from './images/equipo2.jpg';
import equipo3 from './images/equipo3.jpg';
import equipo4 from './images/equipo4.jpg';
import insumos1 from './images/Insumos4.png';
import insumos2 from './images/insumos5.webp';
import insumos3 from './images/insumos6.jpeg';
import insumos4 from './images/insumos7.png';
import politicaDatos from './docs/politicatratamientodatos.pdf';
import politicaDevolucion from './docs/politicadevolucion.pdf';
import politicaGestionAmbiental from './docs/politicagestionambiental.pdf';
import politicaSST from './docs/politicasst.pdf';
import politicaPrevencion from './docs/politicaprevenciontabaco.pdf';
import SEO from './components/SEO';
import { OrganizationSchema, LocalBusinessSchema, WebSiteSchema, BreadcrumbSchema } from './components/StructuredData';

// Fix for default marker icon in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Function to decode JWT token
/**
 * Decodes a JWT token to extract its payload.
 * @param {string} token - The JWT token to decode.
 * @returns {Object|null} The decoded payload or null if decoding fails.
 */
const decodeJWT = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

// API Base URL from environment variable
/**
 * Base URL for the API, configurable via environment variables.
 * Defaults to localhost if not specified.
 */
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost/dismhe/api';

const mockContactInfo = {
  address: 'CCarrera 35 A No. 20 - 99 Barrio La Rivera',
  phones: ['6027350053', '+57 3136284535'],
  email: 'igerenciacomercial@dismhecol.com',
  social: {
    facebook: 'dismhecol',
    instagram: 'dismhecol',
    twitter: 'dismhecol'
  }
};

const mockCarouselImages = [
  banner,
  banner1,
  banner2
];

// --- Components defined OUTSIDE App to prevent re-renders ---

/**
 * Page component for searching CUM (Código Único de Medicamentos) and IUM (Identificador Único de Medicamentos).
 * Allows users to search for products, filter results, and view detailed information in a modal.
 */
const CumIumSearchPage = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetail, setProductDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [filterTitular, setFilterTitular] = useState('');
  const [filterEstado, setFilterEstado] = useState('all');
  const [sortBy, setSortBy] = useState('none');

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setError(null);
    setHasSearched(true);
    setResults([]);
    setFilterTitular('');
    setFilterEstado('all');
    setSortBy('none');

    try {
      const response = await fetch(`${API_BASE_URL}/data/productos?q=${encodeURIComponent(query)}`);

      if (!response.ok) {
        throw new Error('Error al conectar con el servidor');
      }

      const data = await response.json();

      if (data.ok && Array.isArray(data.data)) {
        setResults(data.data);
      } else {
        setResults([]);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Hubo un problema al realizar la búsqueda. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = async (expediente) => {
    setSelectedProduct(expediente);
    setDetailLoading(true);
    setProductDetail(null);

    try {
      const response = await fetch(`${API_BASE_URL}/data/producto/${expediente}`);

      if (!response.ok) {
        throw new Error('Error al obtener detalles del producto');
      }

      const data = await response.json();

      if (data.ok && data.data) {
        setProductDetail(data.data);
      }
    } catch (err) {
      console.error('Detail fetch error:', err);
      setProductDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedProduct(null);
    setProductDetail(null);
  };

  // Get unique titulares for filter dropdown
  const uniqueTitulares = [...new Set(results.map(item => item.titular))].sort();

  // Filter and sort results
  const getFilteredAndSortedResults = () => {
    let filtered = [...results];

    // Apply titular filter
    if (filterTitular) {
      filtered = filtered.filter(item => item.titular === filterTitular);
    }

    // Apply estado filter
    if (filterEstado !== 'all') {
      filtered = filtered.filter(item => item.estadocum === filterEstado);
    }

    // Apply sorting
    if (sortBy === 'titular-asc') {
      filtered.sort((a, b) => a.titular.localeCompare(b.titular));
    } else if (sortBy === 'titular-desc') {
      filtered.sort((a, b) => b.titular.localeCompare(a.titular));
    } else if (sortBy === 'estado-activo') {
      filtered.sort((a, b) => {
        if (a.estadocum === 'Activo' && b.estadocum !== 'Activo') return -1;
        if (a.estadocum !== 'Activo' && b.estadocum === 'Activo') return 1;
        return 0;
      });
    } else if (sortBy === 'estado-inactivo') {
      filtered.sort((a, b) => {
        if (a.estadocum !== 'Activo' && b.estadocum === 'Activo') return -1;
        if (a.estadocum === 'Activo' && b.estadocum !== 'Activo') return 1;
        return 0;
      });
    }

    return filtered;
  };

  const filteredResults = getFilteredAndSortedResults();

  return (
    <div className="container cum-ium-page" style={{ paddingTop: '120px', paddingBottom: '4rem' }}>
      <SEO
        title="Consulta CUM e IUM - Dismhecol | Búsqueda de Medicamentos"
        description="Consulta códigos CUM e IUM de medicamentos en Colombia. Base de datos actualizada de productos farmacéuticos, registros sanitarios y principios activos."
        keywords="consulta CUM, consulta IUM, códigos medicamentos Colombia, registro sanitario, base datos medicamentos"
        canonical="/cum-ium"
      />
      <BreadcrumbSchema items={[
        { name: 'Inicio', path: '/' },
        { name: 'Consulta CUM/IUM', path: '/cum-ium' }
      ]} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="cum-ium-content-wrapper"
      >
        <h1 className="section-title">Consulta de Códigos CUM e IUM</h1>

        <div className="search-card glass" style={{ padding: '2rem', borderRadius: '1rem', marginBottom: '3rem' }}>
          <form onSubmit={handleSearch} className="search-form" style={{ display: 'flex', gap: '1rem', maxWidth: '800px', margin: '0 auto' }}>
            <div className="search-input-wrapper" style={{ position: 'relative', flex: 1 }}>
              <Search className="search-icon" size={20} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por nombre, expediente CUM o IUM..."
                className="form-input"
                style={{ paddingLeft: '3rem', width: '100%' }}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
              style={{ minWidth: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {loading ? <Loader className="animate-spin" size={20} /> : 'Buscar'}
            </button>
          </form>
        </div>

        <div className="results-section">
          {loading && (
            <div className="loading-state" style={{ textAlign: 'center', padding: '3rem' }}>
              <Loader className="animate-spin" size={40} style={{ color: 'var(--primary-red)', margin: '0 auto 1rem' }} />
              <p>Buscando información...</p>
            </div>
          )}

          {error && (
            <div className="error-state glass" style={{ textAlign: 'center', padding: '2rem', borderRadius: '1rem', borderLeft: '4px solid #ef4444', color: '#ef4444' }}>
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && hasSearched && results.length === 0 && (
            <div className="empty-state glass" style={{ textAlign: 'center', padding: '3rem', borderRadius: '1rem' }}>
              <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)' }}>No se encontraron resultados para "{query}"</p>
            </div>
          )}

          {!loading && !error && results.length > 0 && (
            <>
              {/* Filter and Sort Controls */}
              <div className="filter-controls glass" style={{
                padding: '1.5rem',
                borderRadius: '1rem',
                marginBottom: '1.5rem',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                alignItems: 'end'
              }}>
                <div className="form-group">
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Filtrar por Titular
                  </label>
                  <select
                    value={filterTitular}
                    onChange={(e) => setFilterTitular(e.target.value)}
                    className="form-input"
                    style={{ width: '100%' }}
                  >
                    <option value="">Todos los titulares</option>
                    {uniqueTitulares.map((titular, idx) => (
                      <option key={idx} value={titular}>{titular}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Filtrar por Estado
                  </label>
                  <select
                    value={filterEstado}
                    onChange={(e) => setFilterEstado(e.target.value)}
                    className="form-input"
                    style={{ width: '100%' }}
                  >
                    <option value="all">Todos los estados</option>
                    <option value="Activo">Activo</option>
                    <option value="Inactivo">Inactivo</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Ordenar por
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="form-input"
                    style={{ width: '100%' }}
                  >
                    <option value="none">Sin ordenar</option>
                    <option value="titular-asc">Titular (A-Z)</option>
                    <option value="titular-desc">Titular (Z-A)</option>
                    <option value="estado-activo">Estado: Activos primero</option>
                    <option value="estado-inactivo">Estado: Inactivos primero</option>
                  </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                    Mostrando <strong>{filteredResults.length}</strong> de <strong>{results.length}</strong> resultados
                  </span>
                </div>
              </div>

              <div className="results-table-container glass" style={{ borderRadius: '1rem', overflow: 'hidden', overflowX: 'auto' }}>
                <table className="results-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-accent)', borderBottom: '1px solid var(--glass-border)' }}>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold', color: 'var(--primary-red)' }}>Expediente</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold', color: 'var(--primary-red)' }}>Producto</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold', color: 'var(--primary-red)' }}>Titular</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold', color: 'var(--primary-red)' }}>Registro Sanitario</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold', color: 'var(--primary-red)' }}>Estado Registro</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold', color: 'var(--primary-red)' }}>Estado CUM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredResults.map((item, index) => (
                      <motion.tr
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        style={{
                          borderBottom: '1px solid var(--glass-border)',
                          cursor: 'pointer',
                          background: item.estadocum === 'Activo' ? 'rgba(220, 252, 231, 0.3)' : 'transparent',
                          borderLeft: item.estadocum === 'Activo' ? '4px solid #22c55e' : '4px solid transparent'
                        }}
                        whileHover={{ backgroundColor: item.estadocum === 'Activo' ? 'rgba(220, 252, 231, 0.5)' : 'rgba(225, 29, 72, 0.05)' }}
                        onClick={() => handleRowClick(item.expediente)}
                      >
                        <td style={{ padding: '1rem' }}>{item.expediente}</td>
                        <td style={{ padding: '1rem', fontWeight: '500' }}>{item.producto}</td>
                        <td style={{ padding: '1rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>{item.titular}</td>
                        <td style={{ padding: '1rem' }}>{item.registrosanitario}</td>
                        <td style={{ padding: '1rem' }}>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '1rem',
                            fontSize: '0.85rem',
                            background: item.estadoregistro === 'Vigente' ? '#dcfce7' : '#fee2e2',
                            color: item.estadoregistro === 'Vigente' ? '#166534' : '#991b1b'
                          }}>
                            {item.estadoregistro}
                          </span>
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '1rem',
                            fontSize: '0.85rem',
                            background: item.estadocum === 'Activo' ? '#dcfce7' : '#f3f4f6',
                            color: item.estadocum === 'Activo' ? '#166534' : '#4b5563'
                          }}>
                            {item.estadocum}
                          </span>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Product Detail Modal */}
        <AnimatePresence>
          {selectedProduct && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={closeModal}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.8)',
                zIndex: 2000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2rem',
                overflowY: 'auto'
              }}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="modal-content glass"
                style={{
                  background: 'var(--glass-bg)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: '1rem',
                  padding: '2rem',
                  maxWidth: '900px',
                  width: '100%',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  position: 'relative'
                }}
              >
                <button
                  onClick={closeModal}
                  className="modal-close-btn"
                  style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    background: 'var(--primary-red)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: '1.5rem',
                    fontWeight: 'bold'
                  }}
                >
                  ×
                </button>

                {detailLoading && (
                  <div style={{ textAlign: 'center', padding: '3rem' }}>
                    <Loader className="animate-spin" size={40} style={{ color: 'var(--primary-red)', margin: '0 auto 1rem' }} />
                    <p>Cargando detalles...</p>
                  </div>
                )}

                {!detailLoading && productDetail && (
                  <div className="product-detail-content">
                    <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '1.5rem', color: 'var(--primary-red)' }}>
                      {productDetail.producto.producto}
                    </h2>

                    {/* General Information */}
                    <div className="detail-section" style={{ marginBottom: '2rem' }}>
                      <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem', borderBottom: '2px solid var(--primary-red)', paddingBottom: '0.5rem' }}>
                        Información General
                      </h3>

                      {/* Highlighted Codes Section */}
                      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                        <div style={{
                          background: 'var(--primary-red-light)',
                          border: '2px solid var(--primary-red)',
                          padding: '0.75rem 1.25rem',
                          borderRadius: '0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <strong style={{ color: 'var(--primary-red)' }}>Expediente:</strong>
                          <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: 'var(--primary-red)' }}>
                            {productDetail.producto.expediente}
                          </span>
                        </div>

                        {productDetail.producto.ium && (
                          <div style={{
                            background: 'var(--primary-red-light)',
                            border: '2px solid var(--primary-red)',
                            padding: '0.75rem 1.25rem',
                            borderRadius: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            <strong style={{ color: 'var(--primary-red)' }}>IUM:</strong>
                            <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: 'var(--primary-red)' }}>
                              {productDetail.producto.ium}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Other Information Grid */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                        <div>
                          <strong>Titular:</strong> {productDetail.producto.titular}
                        </div>
                        <div>
                          <strong>Registro Sanitario:</strong> {productDetail.producto.registrosanitario}
                        </div>
                        <div>
                          <strong>Estado Registro:</strong>{' '}
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '1rem',
                            fontSize: '0.85rem',
                            background: productDetail.producto.estadoregistro === 'Vigente' ? '#dcfce7' : '#fee2e2',
                            color: productDetail.producto.estadoregistro === 'Vigente' ? '#166534' : '#991b1b'
                          }}>
                            {productDetail.producto.estadoregistro}
                          </span>
                        </div>
                        <div>
                          <strong>Estado CUM:</strong>{' '}
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '1rem',
                            fontSize: '0.85rem',
                            background: productDetail.producto.estadocum === 'Activo' ? '#dcfce7' : '#f3f4f6',
                            color: productDetail.producto.estadocum === 'Activo' ? '#166534' : '#4b5563'
                          }}>
                            {productDetail.producto.estadocum}
                          </span>
                        </div>
                        <div>
                          <strong>Vía de Administración:</strong> {productDetail.producto.viadaministracion}
                        </div>
                        <div>
                          <strong>Forma Farmacéutica:</strong> {productDetail.producto.formafarmaceutica}
                        </div>
                        {productDetail.producto.fechaexp && (
                          <div>
                            <strong>Fecha Expedición:</strong> {new Date(productDetail.producto.fechaexp).toLocaleDateString('es-CO')}
                          </div>
                        )}
                        {productDetail.producto.fechavencimiento && (
                          <div>
                            <strong>Fecha Vencimiento:</strong> {new Date(productDetail.producto.fechavencimiento).toLocaleDateString('es-CO')}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Presentations */}
                    {productDetail.presentaciones && productDetail.presentaciones.length > 0 && (
                      <div className="detail-section" style={{ marginBottom: '2rem' }}>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem', borderBottom: '2px solid var(--primary-red)', paddingBottom: '0.5rem' }}>
                          Presentaciones Comerciales
                        </h3>
                        <div style={{ display: 'grid', gap: '0.75rem' }}>
                          {productDetail.presentaciones.map((pres, idx) => (
                            <div key={idx} style={{
                              background: 'var(--bg-accent)',
                              padding: '0.75rem 1rem',
                              borderRadius: '0.5rem',
                              display: 'grid',
                              gridTemplateColumns: 'auto 1fr auto',
                              gap: '1rem',
                              alignItems: 'center'
                            }}>
                              <span style={{
                                background: 'var(--primary-red-light)',
                                color: 'var(--primary-red)',
                                padding: '0.25rem 0.75rem',
                                borderRadius: '1rem',
                                fontSize: '0.85rem',
                                fontWeight: 'bold',
                                whiteSpace: 'nowrap'
                              }}>
                                {productDetail.producto.expediente}-{pres.consecutivocum}
                              </span>
                              <span>{pres.descripcioncomercial}</span>
                              <span style={{
                                fontSize: '0.9rem',
                                color: 'var(--text-secondary)',
                                fontWeight: '500',
                                whiteSpace: 'nowrap'
                              }}>
                                Cant: {pres.cantidad}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Active Ingredients */}
                    {productDetail.principios_activos && productDetail.principios_activos.length > 0 && (
                      <div className="detail-section" style={{ marginBottom: '2rem' }}>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem', borderBottom: '2px solid var(--primary-red)', paddingBottom: '0.5rem' }}>
                          Principios Activos
                        </h3>
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: 'var(--bg-accent)' }}>
                                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 'bold' }}>Principio Activo</th>
                                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 'bold' }}>Cantidad</th>
                                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 'bold' }}>ATC</th>
                                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 'bold' }}>Descripción ATC</th>
                              </tr>
                            </thead>
                            <tbody>
                              {productDetail.principios_activos.map((pa, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                                  <td style={{ padding: '0.75rem' }}>{pa.principioactivo}</td>
                                  <td style={{ padding: '0.75rem' }}>
                                    {pa.cantidad} {pa.unidadmedida}
                                  </td>
                                  <td style={{ padding: '0.75rem' }}>{pa.atc}</td>
                                  <td style={{ padding: '0.75rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                                    {pa.descripcionatc}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Roles and Modalities */}
                    {productDetail.roles_modalidades && productDetail.roles_modalidades.length > 0 && (
                      <div className="detail-section">
                        <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem', borderBottom: '2px solid var(--primary-red)', paddingBottom: '0.5rem' }}>
                          Fabricantes y Modalidades
                        </h3>
                        <div style={{ display: 'grid', gap: '0.75rem' }}>
                          {productDetail.roles_modalidades.map((rol, idx) => (
                            <div key={idx} style={{
                              background: 'var(--bg-accent)',
                              padding: '1rem',
                              borderRadius: '0.5rem',
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                              gap: '0.5rem'
                            }}>
                              <div>
                                <strong>Nombre:</strong> {rol.nombrerol}
                              </div>
                              <div>
                                <strong>Tipo:</strong> {rol.tiporol}
                              </div>
                              <div>
                                <strong>Modalidad:</strong> {rol.modalidad}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {!detailLoading && !productDetail && (
                  <div style={{ textAlign: 'center', padding: '3rem' }}>
                    <p style={{ color: '#ef4444' }}>No se pudo cargar la información del producto.</p>
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};


/**
 * Home page component.
 * Displays the hero carousel, featured product categories, and an "About Us" preview.
 * 
 * @param {Object} props
 * @param {number} props.currentSlide - Current slide index for the carousel.
 * @param {Function} props.setCurrentSlide - Function to update the carousel slide.
 * @param {Function} props.setCurrentPage - Function to navigate to other pages.
 * @param {Object} props.contactInfo - Contact information to display in schema markup.
 */
const HomePage = ({ currentSlide, setCurrentSlide, setCurrentPage, contactInfo }) => (
  <div className="home-page">
    <SEO
      title="Dismhecol - Distribuidora de Medicamentos y Hospitalarios en Pasto, Nariño"
      description="Distribuidora de medicamentos genéricos, éticos, comerciales, dispositivos médicos e insumos hospitalarios en Pasto, Nariño. Más de 13 años de experiencia sirviendo al sector salud."
      keywords="medicamentos Pasto, dispositivos médicos Nariño, insumos hospitalarios, farmacia Pasto, medicamentos genéricos Colombia, equipos médicos, distribuidora farmacéutica"
      canonical="/"
    />
    <OrganizationSchema contactInfo={contactInfo} />
    <LocalBusinessSchema contactInfo={contactInfo} />
    <WebSiteSchema />

    {/* Hero Carousel */}
    <section className="carousel">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7 }}
          className="carousel-slide"
        >
          <img
            src={mockCarouselImages[currentSlide]}
            alt={`Slide ${currentSlide + 1}`}
            className="carousel-image"
          />
          <div className="carousel-overlay">
            <div className="carousel-content">
              <motion.h1
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="carousel-title"
              >
                Dismhecol
              </motion.h1>
              <motion.p
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="carousel-subtitle"
              >
                Su proveedor confiable de medicamentos y hospitalarios
              </motion.p>
              <motion.button
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ delay: 0.7 }}
                onClick={() => {
                  const element = document.querySelector('.products-section');
                  element?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="btn btn-primary"
              >
                Ver Nuestros Productos
              </motion.button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Carousel Controls */}
      <button
        onClick={() => setCurrentSlide(prev => (prev - 1 + mockCarouselImages.length) % mockCarouselImages.length)}
        className="carousel-control carousel-control-prev"
      >
        <ChevronLeft size={24} />
      </button>
      <button
        onClick={() => setCurrentSlide(prev => (prev + 1) % mockCarouselImages.length)}
        className="carousel-control carousel-control-next"
      >
        <ChevronRight size={24} />
      </button>

      {/* Carousel Indicators */}
      <div className="carousel-indicators">
        {mockCarouselImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`indicator ${index === currentSlide ? 'active' : ''}`}
          />
        ))}
      </div>
    </section>

    {/* Featured Categories */}
    <section className="products-section">
      <div className="container">
        <h2 className="section-title">Nuestros Productos</h2>
        <div className="products-grid">
          {[
            {
              category: 'medicamentos',
              title: 'Medicamentos',
              desc: 'Amplia gama de medicamentos genéricos y de marca. Contamos con medicamentos de alta calidad, respaldados por laboratorios reconocidos.',
              image: medicamentos
            },
            {
              category: 'dispositivos',
              title: 'Equipos Médicos menores',
              desc: 'Equipos y dispositivos de última tecnología para diagnóstico y tratamiento médico.',
              image: dispositivos
            },
            {
              category: 'insumos',
              title: 'Insumos Médicos',
              desc: 'Todo lo necesario para su establecimiento de salud, desde elementos de protección hasta material quirúrgico.',
              image: insumos
            }
          ].map((item, index) => (
            <motion.div
              key={item.category}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: index * 0.2 }}
              className="product-card"
              onClick={() => setCurrentPage(item.category)}
            >
              <div className="product-image-container">
                <img src={item.image} alt={item.title} className="product-image" />
              </div>
              <div className="product-content">
                <h3 className="product-title">{item.title}</h3>
                <p className="product-description">{item.desc}</p>
                <button className="btn btn-primary mt-auto">
                  Ver más detalles
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* About Preview */}
    <section className="about-preview">
      <div className="container">
        <div className="about-content">
          <div className="about-text">
            <h2 className="section-title" style={{ textAlign: 'left', left: 0, transform: 'none' }}>Acerca de Dismhecol</h2>
            <p className="about-description" style={{ marginBottom: '1.5rem', fontSize: '1.1rem', color: 'var(--text-secondary)' }}>
              Somos una empresa con más de 13 años de experiencia en el sector salud, especializada en la comercialización al por mayor de medicamentos genéricos,
              éticos, comerciales, de UCI y alto costo, además de dispositivos médicos, insumos hospitalarios,
              equipos menores, ropa clínica y kits estéticos.
            </p>
            <p className="about-description" style={{ marginBottom: '2rem', fontSize: '1.1rem', color: 'var(--text-secondary)' }}>
              Con sede en Pasto, nos consolidamos como un aliado estratégico y confiable en la cadena de suministro médico del Departamento de Nariño.
              Nuestra solidez financiera, relaciones directas con proveedores y respaldo de marcas reconocidas, nos posicionan como una empresa comprometida con la calidad,
              cumplimiento y eficiencia.
            </p>
            <button
              onClick={() => setCurrentPage('about')}
              className="btn btn-primary"
            >
              Conózcanos más
            </button>
          </div>
          <div className="about-image-container">
            <img
              src={personal}
              alt="Equipo Dismhecol"
              className="about-img"
            />
            <div className="about-stats">
              <div className="stats-number">13+</div>
              <div className="stats-text">Años de experiencia</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
);

/**
 * About Us page component.
 * Displays company history, mission, vision, values, and a photo gallery.
 * Includes a modal for viewing larger versions of gallery images.
 */
const AboutPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const companyImages = [
    { url: require('./images/frente1.png'), alt: 'Fachada de Dismhecol', title: 'Nuestras Instalaciones' },
    { url: require('./images/bodeda.jpeg'), alt: 'Bodega de almacenamiento', title: 'Bodega de Almacenamiento' },
    // { url: require('./images/estantes.jpeg'), alt: 'Estantes de productos', title: 'Organización de Productos' },
    { url: require('./images/mostrador.jpeg'), alt: 'Área de atención', title: 'Área de Atención al Cliente' },
    { url: require('./images/transporte.jpg'), alt: 'Transporte de Mercancia', title: 'Transporte Seguro' }
  ];

  return (
    <div className="container about-page" style={{ paddingTop: '120px', paddingBottom: '4rem' }}>
      <SEO
        title="Nosotros - Dismhecol | Distribuidora de Medicamentos en Pasto"
        description="Conoce la historia de Dismhecol S.A.S, empresa nariñense con más de 13 años de experiencia en distribución de medicamentos y productos hospitalarios en Pasto, Nariño."
        keywords="Dismhecol, historia, empresa nariñense, distribuidora medicamentos Pasto, misión, visión, valores"
        canonical="/about"
      />
      <BreadcrumbSchema items={[
        { name: 'Inicio', path: '/' },
        { name: 'Nosotros', path: '/about' }
      ]} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="about-content-wrapper"
      >
        <h1 className="section-title">Acerca de Dismhecol</h1>

        <div className="about-section glass" style={{ padding: '2rem', borderRadius: '1rem', marginBottom: '3rem' }}>
          <h2 className="about-subtitle" style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>Nuestra Historia</h2>
          <p className="about-paragraph" style={{ marginBottom: '1rem' }}>
            Dismhecol S.A.S es una empresa de calidad y orgullo netamente NARIÑENSE que inicio sus labores en la ciudad de San Juan de Pasto el 20 de febrero de 2012,
            que se viene consolidando en el tiempo con una fuente importante de trabajo y desarrollo para nuestro departamento.
          </p>
          <p className="about-paragraph">
            Nuestro compromiso con la calidad, la ética y el servicio al cliente nos ha
            permitido establecer relaciones duraderas con farmacias, hospitales y clínicas
            en el suroccidente colombiano.
          </p>
        </div>

        {/* Galería de Fotos de la Empresa */}
        <div className="company-gallery-section" style={{ marginBottom: '3rem' }}>
          <h2 className="section-title">Nuestras Instalaciones</h2>
          <div className="gallery-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
            {companyImages.map((image, index) => (
              <motion.div
                key={index}
                className="gallery-item"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.03 }}
                onClick={() => setSelectedImage(image)}
                style={{ cursor: 'pointer', borderRadius: '1rem', overflow: 'hidden', boxShadow: 'var(--shadow-md)' }}
              >
                <div className="gallery-image-container" style={{ height: '250px', position: 'relative' }}>
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="gallery-image"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                    padding: '1rem',
                    color: 'white',
                    fontWeight: 'bold'
                  }}>
                    {image.title}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Modal de imagen ampliada */}
        <AnimatePresence>
          {selectedImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="image-modal-overlay"
              onClick={() => setSelectedImage(null)}
              style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.9)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}
            >
              <motion.div
                className="image-modal-content"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                onClick={(e) => e.stopPropagation()}
                style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}
              >
                <img
                  src={selectedImage.url}
                  alt={selectedImage.alt}
                  className="modal-image"
                  style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: '0.5rem' }}
                />
                <button
                  className="modal-close-btn"
                  onClick={() => setSelectedImage(null)}
                  style={{ position: 'absolute', top: '-40px', right: 0, color: 'white', background: 'none', border: 'none', fontSize: '2rem', cursor: 'pointer' }}
                >
                  ✕
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mission-vision-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', marginBottom: '3rem' }}>
          <div className="mission-card glass" style={{ padding: '2rem', borderRadius: '1rem' }}>
            <h2 className="about-subtitle" style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: 'var(--primary-red)' }}>Misión</h2>
            <p className="about-paragraph">
              Brindar soluciones eficientes y confiables en la comercialización y distribución de productos farmacéuticos, médicos y hospitalarios,
              satisfaciendo las necesidades de las instituciones de salud del país con compromiso, calidad y cumplimiento.
            </p>
          </div>

          <div className="vision-card glass" style={{ padding: '2rem', borderRadius: '1rem' }}>
            <h2 className="about-subtitle" style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: 'var(--primary-red)' }}>Visión</h2>
            <p className="about-paragraph">
              Ser reconocidos como la empresa líder en distribución médica del suroccidente colombiano, destacándonos por nuestra excelencia operativa,
              solidez financiera y relaciones duraderas con nuestros clientes y aliados estratégicos.
            </p>
          </div>
        </div>

        <div className="values-section">
          <h2 className="section-title">Nuestros Valores</h2>
          <div className="values-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
            {[
              { title: 'Calidad', desc: 'Productos certificados y de alta calidad' },
              { title: 'Integridad', desc: 'Transparencia en todas nuestras operaciones' },
              { title: 'Compromiso', desc: 'Dedicados al bienestar de la comunidad' },
              { title: 'Innovación', desc: 'Siempre actualizados con las últimas tecnologías' },
              { title: 'Responsabilidad', desc: 'Cumplimiento de normas y regulaciones' },
              { title: 'Excelencia', desc: 'Servicio superior a nuestros clientes' }
            ].map((value, index) => (
              <motion.div
                key={index}
                whileHover={{ y: -5 }}
                className="value-card glass"
                style={{ padding: '1.5rem', borderRadius: '1rem', textAlign: 'center' }}
              >
                <div className="value-icon" style={{ width: '50px', height: '50px', background: 'var(--primary-red-light)', color: 'var(--primary-red)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem', fontSize: '1.5rem', fontWeight: 'bold' }}>
                  {value.title.charAt(0)}
                </div>
                <h3 className="value-title" style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{value.title}</h3>
                <p className="value-description" style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

/**
 * Contact page component.
 * Displays contact information, department details, social media links, and a location map.
 * 
 * @param {Object} props
 * @param {Object} props.contactInfo - Object containing address, phones, email, social links, and coordinates.
 */
const ContactPage = ({ contactInfo }) => (
  <div className="container contact-page" style={{ paddingTop: '120px', paddingBottom: '4rem' }}>
    <SEO
      title="Contacto - Dismhecol | Contáctanos en Pasto, Nariño"
      description="Contáctanos para solicitar información sobre medicamentos, dispositivos médicos e insumos hospitalarios. Ubicados en Pasto, Nariño. Teléfono, email y ubicación."
      keywords="contacto Dismhecol, teléfono Dismhecol Pasto, dirección Dismhecol, email comercial Dismhecol"
      canonical="/contact"
    />
    <BreadcrumbSchema items={[
      { name: 'Inicio', path: '/' },
      { name: 'Contacto', path: '/contact' }
    ]} />

    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="contact-content-wrapper"
    >
      <h1 className="section-title">Contáctenos</h1>

      <div className="contact-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '3rem' }}>
        <div className="contact-info-section">
          {/* Dirección */}
          <div className="contact-card glass" style={{ padding: '2rem', borderRadius: '1rem', marginBottom: '2rem' }}>
            <div className="contact-item" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              <MapPin className="contact-icon" size={24} style={{ color: 'var(--primary-red)', flexShrink: 0 }} />
              <div>
                <h3 className="contact-label" style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Dirección</h3>
                <p className="contact-text" style={{ color: 'var(--text-secondary)' }}>{contactInfo.address}</p>
              </div>
            </div>

            {/* Teléfono Fijo */}
            {contactInfo.tel_fijo && (
              <div className="contact-item" style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <Phone className="contact-icon" size={24} style={{ color: 'var(--primary-red)', flexShrink: 0 }} />
                <div>
                  <h3 className="contact-label" style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Call Center</h3>
                  <p className="contact-text" style={{ color: 'var(--text-secondary)' }}>{contactInfo.tel_fijo}</p>
                </div>
              </div>
            )}
          </div>

          {/* Departamentos */}
          <div className="contact-card glass" style={{ padding: '2rem', borderRadius: '1rem', marginBottom: '2rem' }}>
            <h2 className="contact-subtitle" style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem', color: 'var(--primary-red)' }}>
              Departamentos
            </h2>

            <div style={{ display: 'grid', gap: '1.5rem' }}>
              {contactInfo.departments && contactInfo.departments.map((dept, index) => (
                <div
                  key={index}
                  className="department-card"
                  style={{
                    background: 'var(--bg-secondary)',
                    padding: '1.25rem',
                    borderRadius: '0.75rem',
                    borderLeft: '4px solid var(--primary-red)',
                    transition: 'var(--transition)'
                  }}
                >
                  <h3 style={{
                    fontWeight: 'bold',
                    marginBottom: '0.75rem',
                    color: 'var(--text-main)',
                    fontSize: '1.1rem'
                  }}>
                    {dept.name}
                  </h3>

                  {dept.phone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <Phone size={16} style={{ color: 'var(--primary-red)' }} />
                      <a
                        href={`tel:${dept.phone}`}
                        style={{
                          color: 'var(--text-secondary)',
                          textDecoration: 'none',
                          fontSize: '0.95rem'
                        }}
                      >
                        {dept.phone}
                      </a>
                    </div>
                  )}

                  {dept.email && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Mail size={16} style={{ color: 'var(--primary-red)' }} />
                      <a
                        href={`mailto:${dept.email}`}
                        style={{
                          color: 'var(--text-secondary)',
                          textDecoration: 'none',
                          fontSize: '0.95rem',
                          wordBreak: 'break-word'
                        }}
                      >
                        {dept.email}
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Redes Sociales */}
          <div className="contact-card glass" style={{ padding: '2rem', borderRadius: '1rem' }}>
            <h3 className="contact-label" style={{ fontWeight: 'bold', marginBottom: '1rem' }}>Síguenos en Redes Sociales</h3>
            <div className="social-links" style={{ display: 'flex', gap: '1rem' }}>
              <a href="https://www.facebook.com/share/17nRm1r3Kc/" className="social-link" style={{ background: 'var(--primary-red)', color: 'white' }}>
                <Facebook size={20} />
              </a>
              <a href="https://www.instagram.com/dismhecolsas/" className="social-link" style={{ background: 'var(--primary-red)', color: 'white' }}>
                <Instagram size={20} />
              </a>
              <a href="https://x.com/dismhecol?t=P3-Pa05qGd9D4Rdp8nLCsg&s=08" className="social-link" style={{ background: 'var(--primary-red)', color: 'white' }}>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a href="#" className="social-link" style={{ background: 'var(--primary-red)', color: 'white' }}>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        <div className="map-section">
          <div className="contact-card glass" style={{ padding: '2rem', borderRadius: '1rem', height: '100%' }}>
            <h2 className="contact-subtitle" style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>Ubicación</h2>

            {contactInfo.coordinates ? (
              <div className="map-container" style={{ height: '400px', borderRadius: '0.5rem', overflow: 'hidden', marginBottom: '1rem' }}>
                <MapContainer
                  center={[contactInfo.coordinates.lat, contactInfo.coordinates.lng]}
                  zoom={15}
                  style={{ height: '100%', width: '100%' }}
                  scrollWheelZoom={false}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={[contactInfo.coordinates.lat, contactInfo.coordinates.lng]}>
                    <Popup>
                      <div style={{ textAlign: 'center' }}>
                        <strong style={{ color: 'var(--primary-red)', fontSize: '1.1rem' }}>DISMHECOL SAS</strong>
                        <br />
                        <span style={{ fontSize: '0.9rem' }}>{contactInfo.address}</span>
                      </div>
                    </Popup>
                  </Marker>
                </MapContainer>
              </div>
            ) : (
              <div className="map-placeholder" style={{ background: '#e5e7eb', height: '400px', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
                <p className="map-text">Cargando ubicación...</p>
              </div>
            )}

            <p className="map-description" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              Visítenos en nuestra ubicación. Estamos disponibles para atenderle y resolver todas sus consultas.
            </p>

            {contactInfo.coordinates && (
              <a
                href={`https://www.google.com/maps?q=${contactInfo.coordinates.lat},${contactInfo.coordinates.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
                style={{ marginTop: '1rem', display: 'inline-block', textDecoration: 'none' }}
              >
                Ver en Google Maps
              </a>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  </div>
);

/**
 * Quote Request page component.
 * Provides a form for users to request product quotations.
 * Handles form submission to the backend API.
 */
const QuotePage = () => {
  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    products: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...quoteForm,
          to: 'comercial@dismhecol.com'
        }),
      });

      const data = await response.json();

      if (response.ok && data.ok) {
        setSubmitStatus('success');
        setQuoteForm({
          name: '',
          email: '',
          phone: '',
          company: '',
          products: '',
          message: ''
        });
        // Clear success message after 5 seconds
        setTimeout(() => setSubmitStatus(null), 5000);
      } else {
        setSubmitStatus('error');
        setErrorMessage(data.message || 'Error al enviar la solicitud. Por favor, intente nuevamente.');
      }
    } catch (error) {
      console.error('Quote submission error:', error);
      setSubmitStatus('error');
      setErrorMessage('Error al conectar con el servidor. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container quote-page" style={{ paddingTop: '120px', paddingBottom: '4rem' }}>
      <SEO
        title="Cotización - Dismhecol | Solicita tu Cotización"
        description="Solicita cotización de medicamentos, dispositivos médicos e insumos hospitalarios. Respuesta rápida y precios competitivos en Pasto, Nariño."
        keywords="cotización medicamentos Pasto, solicitar cotización dispositivos médicos, precios insumos hospitalarios"
        canonical="/quote"
      />
      <BreadcrumbSchema items={[
        { name: 'Inicio', path: '/' },
        { name: 'Cotización', path: '/quote' }
      ]} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="quote-content-wrapper"
      >
        <h1 className="section-title">Solicitud de Cotización</h1>

        <div className="quote-card glass" style={{ padding: '3rem', borderRadius: '1rem', maxWidth: '800px', margin: '0 auto' }}>
          <form onSubmit={handleSubmit} className="quote-form">
            <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
              <div className="form-group">
                <label className="form-label">Nombre Completo *</label>
                <input
                  type="text"
                  required
                  value={quoteForm.name}
                  onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Correo Electrónico *</label>
                <input
                  type="email"
                  required
                  value={quoteForm.email}
                  onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
              <div className="form-group">
                <label className="form-label">Teléfono *</label>
                <input
                  type="tel"
                  required
                  value={quoteForm.phone}
                  onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Empresa</label>
                <input
                  type="text"
                  value={quoteForm.company}
                  onChange={(e) => setQuoteForm({ ...quoteForm, company: e.target.value })}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Productos de Interés *</label>
              <textarea
                required
                rows={4}
                value={quoteForm.products}
                onChange={(e) => setQuoteForm({ ...quoteForm, products: e.target.value })}
                className="form-textarea"
                placeholder="Describa los productos que necesita cotizar..."
              ></textarea>
            </div>

            <div className="form-group">
              <label className="form-label">Mensaje Adicional</label>
              <textarea
                rows={4}
                value={quoteForm.message}
                onChange={(e) => setQuoteForm({ ...quoteForm, message: e.target.value })}
                className="form-textarea"
                placeholder="Cualquier información adicional que desee compartir..."
              ></textarea>
            </div>

            {/* Success Message */}
            {submitStatus === 'success' && (
              <div className="alert alert-success" style={{
                padding: '1rem',
                borderRadius: '0.5rem',
                background: '#dcfce7',
                border: '1px solid #22c55e',
                color: '#166534',
                marginTop: '1rem'
              }}>
                ✓ Solicitud de cotización enviada exitosamente. Nos contactaremos pronto.
              </div>
            )}

            {/* Error Message */}
            {submitStatus === 'error' && (
              <div className="alert alert-error" style={{
                padding: '1rem',
                borderRadius: '0.5rem',
                background: '#fee2e2',
                border: '1px solid #ef4444',
                color: '#991b1b',
                marginTop: '1rem'
              }}>
                ✗ {errorMessage}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary quote-submit-btn"
              style={{ width: '100%', marginTop: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader className="animate-spin" size={20} />
                  Enviando...
                </>
              ) : (
                'Enviar Solicitud de Cotización'
              )}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

/**
 * PQRS (Peticiones, Quejas, Reclamos y Sugerencias) page component.
 * Allows users to submit different types of requests or feedback.
 * Handles form submission to the backend API.
 */
const PQRSPage = () => {
  const [pqrsForm, setPqrsForm] = useState({
    name: '',
    email: '',
    phone: '',
    type: 'peticion',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/pqrs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...pqrsForm,
          to: 'comercial@dismhecol.com'
        }),
      });

      const data = await response.json();

      if (response.ok && data.ok) {
        setSubmitStatus('success');
        setPqrsForm({
          name: '',
          email: '',
          phone: '',
          type: 'peticion',
          subject: '',
          message: ''
        });
        // Clear success message after 5 seconds
        setTimeout(() => setSubmitStatus(null), 5000);
      } else {
        setSubmitStatus('error');
        setErrorMessage(data.message || 'Error al enviar el PQRS. Por favor, intente nuevamente.');
      }
    } catch (error) {
      console.error('PQRS submission error:', error);
      setSubmitStatus('error');
      setErrorMessage('Error al conectar con el servidor. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const pqrsTypes = [
    { value: 'peticion', label: 'Petición' },
    { value: 'queja', label: 'Queja' },
    { value: 'reclamo', label: 'Reclamo' },
    { value: 'sugerencia', label: 'Sugerencia' },
    { value: 'felicitaciones', label: 'Felicitaciones' }
  ];

  return (
    <div className="container pqrs-page" style={{ paddingTop: '120px', paddingBottom: '4rem' }}>
      <SEO
        title="PQRS - Dismhecol | Peticiones, Quejas, Reclamos y Sugerencias"
        description="Sistema de PQRS de Dismhecol. Envía tus peticiones, quejas, reclamos o sugerencias. Tu opinión es importante para nosotros."
        keywords="PQRS Dismhecol, quejas Dismhecol, sugerencias, peticiones, reclamos"
        canonical="/pqrs"
      />
      <BreadcrumbSchema items={[
        { name: 'Inicio', path: '/' },
        { name: 'PQRS', path: '/pqrs' }
      ]} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="pqrs-content-wrapper"
      >
        <h1 className="section-title">PQRS</h1>
        <p style={{ textAlign: 'center', fontSize: '1.1rem', color: 'var(--text-secondary)', marginBottom: '3rem', maxWidth: '800px', margin: '0 auto 3rem' }}>
          Peticiones, Quejas, Reclamos y Sugerencias
        </p>

        <div className="pqrs-card glass" style={{ padding: '3rem', borderRadius: '1rem', maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ marginBottom: '2rem', padding: '1.5rem', background: 'var(--bg-accent)', borderRadius: '0.75rem', borderLeft: '4px solid var(--primary-red)' }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.75rem', color: 'var(--primary-red)' }}>
              ¿Cómo podemos ayudarte?
            </h3>
            <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              Tu opinión es muy importante para nosotros. Utiliza este formulario para enviarnos tus peticiones, quejas, reclamos o sugerencias.
              Nos comprometemos a responder en el menor tiempo posible.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="pqrs-form">
            <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
              <div className="form-group">
                <label className="form-label">Nombre Completo *</label>
                <input
                  type="text"
                  required
                  value={pqrsForm.name}
                  onChange={(e) => setPqrsForm({ ...pqrsForm, name: e.target.value })}
                  className="form-input"
                  placeholder="Ingrese su nombre completo"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Correo Electrónico *</label>
                <input
                  type="email"
                  required
                  value={pqrsForm.email}
                  onChange={(e) => setPqrsForm({ ...pqrsForm, email: e.target.value })}
                  className="form-input"
                  placeholder="correo@ejemplo.com"
                />
              </div>
            </div>

            <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
              <div className="form-group">
                <label className="form-label">Teléfono *</label>
                <input
                  type="tel"
                  required
                  value={pqrsForm.phone}
                  onChange={(e) => setPqrsForm({ ...pqrsForm, phone: e.target.value })}
                  className="form-input"
                  placeholder="Número de contacto"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Tipo de Solicitud *</label>
                <select
                  required
                  value={pqrsForm.type}
                  onChange={(e) => setPqrsForm({ ...pqrsForm, type: e.target.value })}
                  className="form-input"
                  style={{ cursor: 'pointer' }}
                >
                  {pqrsTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label className="form-label">Asunto *</label>
              <input
                type="text"
                required
                value={pqrsForm.subject}
                onChange={(e) => setPqrsForm({ ...pqrsForm, subject: e.target.value })}
                className="form-input"
                placeholder="Resuma brevemente su solicitud"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Mensaje *</label>
              <textarea
                required
                rows={6}
                value={pqrsForm.message}
                onChange={(e) => setPqrsForm({ ...pqrsForm, message: e.target.value })}
                className="form-textarea"
                placeholder="Describa detalladamente su petición, queja, reclamo o sugerencia..."
              ></textarea>
            </div>

            {/* Success Message */}
            {submitStatus === 'success' && (
              <div className="alert alert-success" style={{
                padding: '1rem',
                borderRadius: '0.5rem',
                background: '#dcfce7',
                border: '1px solid #22c55e',
                color: '#166534',
                marginTop: '1rem'
              }}>
                ✓ Su solicitud ha sido enviada exitosamente. Nos pondremos en contacto con usted pronto.
              </div>
            )}

            {/* Error Message */}
            {submitStatus === 'error' && (
              <div className="alert alert-error" style={{
                padding: '1rem',
                borderRadius: '0.5rem',
                background: '#fee2e2',
                border: '1px solid #ef4444',
                color: '#991b1b',
                marginTop: '1rem'
              }}>
                ✗ {errorMessage}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary pqrs-submit-btn"
              style={{ width: '100%', marginTop: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader className="animate-spin" size={20} />
                  Enviando...
                </>
              ) : (
                'Enviar PQRS'
              )}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

/**
 * Privacy Policy page component.
 * Displays the company's privacy policies regarding data collection and usage.
 */
const PrivacyPage = () => (
  <div className="container privacy-page" style={{ paddingTop: '120px', paddingBottom: '4rem' }}>
    <div className="privacy-content-wrapper">
      <h1 className="section-title">Políticas de Privacidad</h1>

      <div className="privacy-card glass" style={{ padding: '3rem', borderRadius: '1rem' }}>
        <section className="privacy-section" style={{ marginBottom: '2rem' }}>
          <h2 className="privacy-subtitle" style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>1. Información que Recopilamos</h2>
          <p className="privacy-paragraph">
            Recopilamos información personal que nos proporciona voluntariamente cuando
            utiliza nuestros servicios, incluyendo su nombre, dirección de correo
            electrónico, número de teléfono y datos de contacto.
          </p>
        </section>

        <section className="privacy-section" style={{ marginBottom: '2rem' }}>
          <h2 className="privacy-subtitle" style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>2. Uso de la Información</h2>
          <p className="privacy-paragraph">
            Utilizamos su información personal para:
          </p>
          <ul className="privacy-list" style={{ listStyle: 'disc', paddingLeft: '1.5rem', marginTop: '0.5rem' }}>
            <li>Procesar sus solicitudes de cotización y pedidos</li>
            <li>Mejorar nuestros servicios y experiencia de usuario</li>
            <li>Enviar información sobre productos y promociones</li>
            <li>Cumplir con obligaciones legales y regulatorias</li>
          </ul>
        </section>
      </div>
    </div>
  </div>
);

/**
 * PoliciesPage Component
 * Displays company policies including Data Treatment, Health & Safety, and Return Policies.
 * 
 * Features:
 * - Displays policy summaries in cards
 * - Provides buttons to view full PDF documents for specific policies
 * - Uses a modal with an iframe to render PDF files
 * 
 * To add a new policy with PDF:
 * 1. Import the PDF file at the top of the file
 * 2. Add a new section in the JSX
 * 3. Add a button that calls setSelectedPdf(yourNewPdfVariable)
 */
const PoliciesPage = () => {
  // State to manage the currently selected PDF for the modal viewer
  // If null, the modal is closed. If set to a PDF import, the modal is open.
  const [selectedPdf, setSelectedPdf] = useState(null);

  return (
    <div className="container policies-page" style={{ paddingTop: '120px', paddingBottom: '4rem' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="policies-content-wrapper"
      >
        <h1 className="section-title">Políticas</h1>

        {/* Políticas de Tratamiento de Datos */}
        <div className="policy-card glass" style={{ padding: '2.5rem', borderRadius: '1rem', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
            <h2 className="policy-title" style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '1.5rem', color: 'var(--primary-red)' }}>
              Políticas de Tratamiento de Datos
            </h2>
            <button
              onClick={() => setSelectedPdf(politicaDatos)}
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <Search size={18} />
              Ver Documento Completo
            </button>
          </div>

          <section className="policy-section" style={{ marginBottom: '2rem' }}>
            <h3 className="policy-subtitle" style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>1. Responsable del Tratamiento</h3>
            <p className="policy-paragraph" style={{ lineHeight: '1.6', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
              DISMHECOL S.A.S., identificada con NIT, con domicilio en Carrera 35 A No. 20 - 99 Barrio La Rivera,
              San Juan de Pasto, Nariño, es responsable del tratamiento de los datos personales recopilados a través de sus canales de atención.
            </p>
          </section>

          <section className="policy-section" style={{ marginBottom: '2rem' }}>
            <h3 className="policy-subtitle" style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>2. Finalidad del Tratamiento</h3>
            <p className="policy-paragraph" style={{ lineHeight: '1.6', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              Los datos personales recopilados serán utilizados para:
            </p>
            <ul className="policy-list" style={{ listStyle: 'disc', paddingLeft: '1.5rem', marginTop: '0.5rem', lineHeight: '1.8' }}>
              <li>Gestionar solicitudes de cotización y pedidos de productos farmacéuticos y médicos</li>
              <li>Mantener comunicación comercial y envío de información sobre productos y servicios</li>
              <li>Cumplir con obligaciones legales y regulatorias del sector salud</li>
              <li>Realizar estudios de mercado y mejora de servicios</li>
              <li>Gestionar procesos de facturación y cobranza</li>
            </ul>
          </section>

          <section className="policy-section" style={{ marginBottom: '2rem' }}>
            <h3 className="policy-subtitle" style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>3. Derechos del Titular</h3>
            <p className="policy-paragraph" style={{ lineHeight: '1.6', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              Como titular de los datos personales, usted tiene derecho a:
            </p>
            <ul className="policy-list" style={{ listStyle: 'disc', paddingLeft: '1.5rem', marginTop: '0.5rem', lineHeight: '1.8' }}>
              <li>Conocer, actualizar y rectificar sus datos personales</li>
              <li>Solicitar prueba de la autorización otorgada para el tratamiento</li>
              <li>Ser informado sobre el uso que se ha dado a sus datos personales</li>
              <li>Revocar la autorización y/o solicitar la supresión de datos cuando sea procedente</li>
              <li>Acceder de forma gratuita a sus datos personales objeto de tratamiento</li>
            </ul>
          </section>

          <section className="policy-section" style={{ marginBottom: '2rem' }}>
            <h3 className="policy-subtitle" style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>4. Seguridad de la Información</h3>
            <p className="policy-paragraph" style={{ lineHeight: '1.6', color: 'var(--text-secondary)' }}>
              DISMHECOL S.A.S. implementa medidas técnicas, humanas y administrativas necesarias para proteger los datos personales
              y evitar su adulteración, pérdida, consulta, uso o acceso no autorizado o fraudulento.
            </p>
          </section>

          <section className="policy-section">
            <h3 className="policy-subtitle" style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>5. Contacto</h3>
            <p className="policy-paragraph" style={{ lineHeight: '1.6', color: 'var(--text-secondary)' }}>
              Para ejercer sus derechos o realizar consultas sobre el tratamiento de sus datos personales, puede contactarnos a través de
              nuestros canales de atención disponibles en la sección de contacto.
            </p>
          </section>
        </div>

        {/* Políticas de Seguridad y Salud en el Trabajo */}
        <div className="policy-card glass" style={{ padding: '2.5rem', borderRadius: '1rem', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
            <h2 className="policy-title" style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '1.5rem', color: 'var(--primary-red)' }}>
              Políticas de Seguridad y Salud en el Trabajo
            </h2>
            <button
              onClick={() => setSelectedPdf(politicaSST)}
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <Search size={18} />
              Ver Documento Completo
            </button>
          </div>

          <section className="policy-section" style={{ marginBottom: '2rem' }}>
            <h3 className="policy-subtitle" style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>1. Compromiso</h3>
            <p className="policy-paragraph" style={{ lineHeight: '1.6', color: 'var(--text-secondary)' }}>
              DISMHECOL S.A.S. está comprometida con la protección de la seguridad y salud de todos sus trabajadores,
              contratistas y visitantes, mediante la identificación, evaluación y control de los peligros y riesgos asociados
              a las actividades de comercialización de productos farmacéuticos y médicos.
            </p>
          </section>

          <section className="policy-section" style={{ marginBottom: '2rem' }}>
            <h3 className="policy-subtitle" style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>2. Objetivos</h3>
            <ul className="policy-list" style={{ listStyle: 'disc', paddingLeft: '1.5rem', lineHeight: '1.8' }}>
              <li>Promover y mantener ambientes de trabajo seguros y saludables</li>
              <li>Prevenir lesiones y enfermedades laborales</li>
              <li>Cumplir con la legislación vigente en materia de seguridad y salud en el trabajo</li>
              <li>Fomentar la cultura de autocuidado y prevención en todos los niveles de la organización</li>
              <li>Mejorar continuamente el Sistema de Gestión de Seguridad y Salud en el Trabajo</li>
            </ul>
          </section>

          <section className="policy-section" style={{ marginBottom: '2rem' }}>
            <h3 className="policy-subtitle" style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>3. Responsabilidades</h3>
            <p className="policy-paragraph" style={{ lineHeight: '1.6', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              La alta dirección se compromete a:
            </p>
            <ul className="policy-list" style={{ listStyle: 'disc', paddingLeft: '1.5rem', marginTop: '0.5rem', lineHeight: '1.8' }}>
              <li>Proveer los recursos necesarios para la implementación del sistema de gestión</li>
              <li>Garantizar la participación de los trabajadores en el sistema</li>
              <li>Realizar seguimiento y medición del desempeño del sistema</li>
              <li>Investigar los incidentes y accidentes de trabajo</li>
            </ul>
          </section>

          <section className="policy-section">
            <h3 className="policy-subtitle" style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>4. Prevención de Riesgos</h3>
            <p className="policy-paragraph" style={{ lineHeight: '1.6', color: 'var(--text-secondary)' }}>
              Se implementan controles específicos para la manipulación, almacenamiento y distribución de productos farmacéuticos,
              asegurando condiciones ergonómicas y de seguridad industrial adecuadas.
            </p>
          </section>
        </div>

        {/* Políticas de Gestión Ambiental */}
        <div className="policy-card glass" style={{ padding: '2.5rem', borderRadius: '1rem', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
            <h2 className="policy-title" style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '1.5rem', color: 'var(--primary-red)' }}>
              Política de Gestión Ambiental
            </h2>
            <button
              onClick={() => setSelectedPdf(politicaGestionAmbiental)}
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <Search size={18} />
              Ver Documento Completo
            </button>
          </div>

          <section className="policy-section" style={{ marginBottom: '2rem' }}>
            <p className="policy-paragraph" style={{ lineHeight: '1.6', color: 'var(--text-secondary)' }}>
              DISMHECOL S.A.S. se compromete a desarrollar sus operaciones de comercialización y distribución de
              productos farmacéuticos y médicos en armonía con el medio ambiente, garantizando el cumplimiento de la
              normatividad ambiental vigente y promoviendo el uso sostenible de los recursos.
            </p>
          </section>
        </div>

        {/* Políticas de Prevención de Consumo */}
        <div className="policy-card glass" style={{ padding: '2.5rem', borderRadius: '1rem', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
            <h2 className="policy-title" style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '1.5rem', color: 'var(--primary-red)' }}>
              Política de Prevención de Consumo de Alcohol, Tabaco y Drogas
            </h2>
            <button
              onClick={() => setSelectedPdf(politicaPrevencion)}
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <Search size={18} />
              Ver Documento Completo
            </button>
          </div>

          <section className="policy-section" style={{ marginBottom: '2rem' }}>
            <p className="policy-paragraph" style={{ lineHeight: '1.6', color: 'var(--text-secondary)' }}>
              DISMHECOL S.A.S. tiene como objetivo promover, prevenir y fomentar el bienestar de los trabajadores,
              contratistas y demás partes interesadas, mediante la implementación de estilos de vida saludables y
              ambientes de trabajo libres de humo, alcohol y drogas.
            </p>
          </section>
        </div>


        {/* Políticas de Devoluciones */}
        <div className="policy-card glass" style={{ padding: '2.5rem', borderRadius: '1rem', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
            <h2 className="policy-title" style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '1.5rem', color: 'var(--primary-red)' }}>
              Políticas de Devoluciones
            </h2>
            <button
              onClick={() => setSelectedPdf(politicaDevolucion)}
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <Search size={18} />
              Ver Documento Completo
            </button>
          </div>

          <section className="policy-section" style={{ marginBottom: '2rem' }}>
            <h3 className="policy-subtitle" style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>1. Condiciones Generales</h3>
            <p className="policy-paragraph" style={{ lineHeight: '1.6', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              DISMHECOL S.A.S. acepta devoluciones de productos bajo las siguientes condiciones:
            </p>
            <ul className="policy-list" style={{ listStyle: 'disc', paddingLeft: '1.5rem', marginTop: '0.5rem', lineHeight: '1.8' }}>
              <li>El producto debe estar en su empaque original, sin abrir y en perfectas condiciones</li>
              <li>La solicitud de devolución debe realizarse dentro de los 30 días calendario siguientes a la recepción del producto</li>
              <li>Se debe presentar la factura o documento equivalente de compra</li>
              <li>Los productos controlados están sujetos a regulaciones especiales de devolución</li>
            </ul>
          </section>

          <section className="policy-section" style={{ marginBottom: '2rem' }}>
            <h3 className="policy-subtitle" style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>2. Productos No Retornables</h3>
            <p className="policy-paragraph" style={{ lineHeight: '1.6', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              No se aceptan devoluciones de:
            </p>
            <ul className="policy-list" style={{ listStyle: 'disc', paddingLeft: '1.5rem', marginTop: '0.5rem', lineHeight: '1.8' }}>
              <li>Medicamentos que requieren cadena de frío una vez entregados</li>
              <li>Productos con empaques violados o abiertos</li>
              <li>Medicamentos con fecha de vencimiento próxima (menor a 6 meses)</li>
              <li>Productos fabricados bajo pedido especial</li>
              <li>Dispositivos médicos de un solo uso</li>
            </ul>
          </section>

          <section className="policy-section" style={{ marginBottom: '2rem' }}>
            <h3 className="policy-subtitle" style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>3. Proceso de Devolución</h3>
            <p className="policy-paragraph" style={{ lineHeight: '1.6', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              Para realizar una devolución:
            </p>
            <ol className="policy-list" style={{ listStyle: 'decimal', paddingLeft: '1.5rem', marginTop: '0.5rem', lineHeight: '1.8' }}>
              <li>Contacte a nuestro departamento de servicio al cliente</li>
              <li>Proporcione el número de factura y detalles del producto</li>
              <li>Espere la autorización de devolución</li>
              <li>Empaque el producto adecuadamente con la documentación requerida</li>
              <li>Coordine la recolección o entrega del producto</li>
            </ol>
          </section>

          <section className="policy-section" style={{ marginBottom: '2rem' }}>
            <h3 className="policy-subtitle" style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>4. Reembolsos y Cambios</h3>
            <p className="policy-paragraph" style={{ lineHeight: '1.6', color: 'var(--text-secondary)' }}>
              Una vez aprobada la devolución, procesaremos el reembolso o cambio en un plazo de 15 días hábiles.
              Los reembolsos se realizarán mediante el mismo método de pago utilizado en la compra original.
              En caso de productos defectuosos o errores en el despacho, DISMHECOL asumirá los costos de transporte.
            </p>
          </section>

          <section className="policy-section">
            <h3 className="policy-subtitle" style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>5. Garantía de Calidad</h3>
            <p className="policy-paragraph" style={{ lineHeight: '1.6', color: 'var(--text-secondary)' }}>
              Todos nuestros productos cuentan con garantía de calidad. Si detecta algún defecto de fabricación o problema con el producto,
              contáctenos de inmediato. Trabajamos con laboratorios y fabricantes reconocidos para garantizar la calidad de todos nuestros productos.
            </p>
          </section>
        </div>

        {/* Contact CTA */}
        <div className="policy-cta glass" style={{ padding: '2rem', borderRadius: '1rem', textAlign: 'center', background: 'var(--bg-accent)' }}>
          <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>¿Tiene alguna pregunta sobre nuestras políticas?</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
            Nuestro equipo está disponible para resolver todas sus dudas y brindarle la información que necesite.
          </p>
          <button
            onClick={() => window.location.hash = '#contact'}
            className="btn btn-primary"
          >
            Contáctenos
          </button>
        </div>
        {/* 
        PDF Modal Viewer 
        Renders an iframe within a modal to display the selected PDF document.
        The modal is conditionally rendered based on the 'selectedPdf' state.
      */}
        <AnimatePresence>
          {selectedPdf && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => setSelectedPdf(null)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.8)',
                zIndex: 2000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2rem'
              }}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="modal-content glass"
                style={{
                  background: 'white',
                  borderRadius: '1rem',
                  padding: '0',
                  maxWidth: '1000px',
                  width: '100%',
                  height: '90vh',
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <div style={{
                  padding: '1rem',
                  borderBottom: '1px solid #eee',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: 'var(--bg-secondary)'
                }}>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 'bold' }}>Visualizador de Documentos</h3>
                  <button
                    onClick={() => setSelectedPdf(null)}
                    style={{
                      background: 'var(--primary-red)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fontSize: '1.2rem'
                    }}
                  >
                    ×
                  </button>
                </div>
                {/* iframe used to render the PDF using the browser's native PDF viewer */}
                <iframe
                  src={selectedPdf}
                  title="PDF Viewer"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div >
    </div >
  );
};

/**
 * Admin Panel component.
 * Allows authorized users to update company information.
 * 
 * @param {Object} props
 * @param {Function} props.handleLogout - Function to handle user logout.
 * @param {Object} props.contactInfo - Current company contact information.
 * @param {Function} props.setContactInfo - Function to update contact information state.
 * @param {Object} props.userData - Data of the currently logged-in user.
 */
const AdminPanel = ({ handleLogout, contactInfo, setContactInfo, userData }) => {
  const [companyForm, setCompanyForm] = useState({
    empresa: '',
    direccion: '',
    tel_fijo: '',
    tel_gerente: '',
    email_gerencia: '',
    tel_comercial: '',
    email_comercial: '',
    tel_asesor: '',
    email_asesor: '',
    email_servcliente: '',
    email_contabilidad: '',
    tel_bodega: '',
    email_bodega: '',
    email_directecnica: '',
    georeferencia: ''
  });
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateMessage, setUpdateMessage] = useState({ type: '', text: '' });

  // Load company info when contact info changes
  useEffect(() => {
    if (contactInfo) {
      setCompanyForm({
        empresa: contactInfo.empresa || '',
        direccion: contactInfo.address || '',
        tel_fijo: contactInfo.tel_fijo || '',
        tel_gerente: contactInfo.departments?.find(d => d.name === 'Gerencia Comercial')?.phone || '',
        email_gerencia: contactInfo.departments?.find(d => d.name === 'Gerencia Comercial')?.email || '',
        tel_comercial: contactInfo.departments?.find(d => d.name === 'Comercial')?.phone || '',
        email_comercial: contactInfo.departments?.find(d => d.name === 'Comercial')?.email || '',
        tel_asesor: contactInfo.departments?.find(d => d.name === 'Asesor Comercial')?.phone || '',
        email_asesor: contactInfo.departments?.find(d => d.name === 'Asesor Comercial')?.email || '',
        email_servcliente: contactInfo.departments?.find(d => d.name === 'Servicio al Cliente')?.email || '',
        email_contabilidad: contactInfo.departments?.find(d => d.name === 'Contabilidad')?.email || '',
        tel_bodega: contactInfo.departments?.find(d => d.name === 'Bodega')?.phone || '',
        email_bodega: contactInfo.departments?.find(d => d.name === 'Bodega')?.email || '',
        email_directecnica: contactInfo.departments?.find(d => d.name === 'Dirección Técnica')?.email || '',
        georeferencia: contactInfo.coordinates ? `${contactInfo.coordinates.lat}, ${contactInfo.coordinates.lng}` : ''
      });
    }
  }, [contactInfo]);

  const handleUpdateCompanyInfo = async (e) => {
    e.preventDefault();
    setUpdateLoading(true);
    setUpdateMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setUpdateMessage({ type: 'error', text: 'No se encontró el token de autenticación.' });
        setUpdateLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/data/informacion/1`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(companyForm)
      });

      const data = await response.json();

      if (data.ok) {
        setUpdateMessage({ type: 'success', text: 'Información de la empresa actualizada exitosamente.' });
        // Refresh company info
        window.location.reload();
      } else {
        setUpdateMessage({ type: 'error', text: data.message || 'Error al actualizar la información.' });
      }
    } catch (error) {
      console.error('Error updating company info:', error);
      setUpdateMessage({ type: 'error', text: 'Error al conectar con el servidor.' });
    } finally {
      setUpdateLoading(false);
    }
  };

  return (
    <div className="container admin-page" style={{ paddingTop: '120px', paddingBottom: '4rem' }}>
      <div className="admin-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1 className="admin-title" style={{ fontSize: '2rem', fontWeight: 'bold' }}>Panel de Administración</h1>
        <button
          onClick={handleLogout}
          className="btn btn-primary"
        >
          Cerrar Sesión
        </button>
      </div>

      {/* User Info Card */}
      {userData && (
        <div className="user-info-card glass" style={{
          padding: '1.5rem',
          borderRadius: '1rem',
          marginBottom: '2rem',
          background: 'linear-gradient(135deg, rgba(225, 29, 72, 0.1) 0%, rgba(225, 29, 72, 0.05) 100%)',
          borderLeft: '4px solid var(--primary-red)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: 'var(--primary-red)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '1.5rem',
              fontWeight: 'bold'
            }}>
              {userData.email ? userData.email.charAt(0).toUpperCase() : 'U'}
            </div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '0.25rem', color: 'var(--text-primary)' }}>
                Bienvenido
              </h3>
              <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                <strong>Email:</strong> {userData.email}
              </p>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                {userData.idrollsusu && (
                  <span style={{
                    padding: '0.25rem 0.75rem',
                    background: 'var(--primary-red)',
                    color: 'white',
                    borderRadius: '1rem',
                    fontSize: '0.85rem',
                    fontWeight: '500'
                  }}>
                    Rol ID: {userData.idrollsusu}
                  </span>
                )}
                {userData.id && (
                  <span style={{
                    padding: '0.25rem 0.75rem',
                    background: 'var(--bg-accent)',
                    color: 'var(--text-secondary)',
                    borderRadius: '1rem',
                    fontSize: '0.85rem'
                  }}>
                    Usuario ID: {userData.id}
                  </span>
                )}
                {userData.exp && (
                  <span style={{
                    padding: '0.25rem 0.75rem',
                    background: '#dcfce7',
                    color: '#166534',
                    borderRadius: '1rem',
                    fontSize: '0.85rem'
                  }}>
                    Sesión válida hasta: {new Date(userData.exp * 1000).toLocaleString('es-CO')}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="admin-panel glass" style={{ padding: '2rem', borderRadius: '1rem' }}>

        {/* Company Information Form */}
        <div className="admin-company-section">
          <h2 className="admin-section-title" style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>Información de la Empresa</h2>

          {updateMessage.text && (
            <div style={{
              padding: '1rem',
              marginBottom: '1.5rem',
              borderRadius: '0.5rem',
              background: updateMessage.type === 'success' ? '#dcfce7' : '#fee2e2',
              color: updateMessage.type === 'success' ? '#166534' : '#991b1b',
              border: `1px solid ${updateMessage.type === 'success' ? '#86efac' : '#fca5a5'}`
            }}>
              {updateMessage.text}
            </div>
          )}

          <form onSubmit={handleUpdateCompanyInfo} className="admin-company-form">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
              {/* Información General */}
              <div style={{ gridColumn: '1 / -1' }}>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '1rem', color: 'var(--primary-red)' }}>Información General</h3>
              </div>

              <div className="form-group">
                <label className="form-label">Nombre de la Empresa</label>
                <input
                  type="text"
                  value={companyForm.empresa}
                  onChange={(e) => setCompanyForm({ ...companyForm, empresa: e.target.value })}
                  className="form-input"
                  required
                  disabled={updateLoading}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Dirección</label>
                <input
                  type="text"
                  value={companyForm.direccion}
                  onChange={(e) => setCompanyForm({ ...companyForm, direccion: e.target.value })}
                  className="form-input"
                  disabled={updateLoading}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Teléfono Fijo</label>
                <input
                  type="tel"
                  value={companyForm.tel_fijo}
                  onChange={(e) => setCompanyForm({ ...companyForm, tel_fijo: e.target.value })}
                  className="form-input"
                  disabled={updateLoading}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Georeferencia (lat, lng)</label>
                <input
                  type="text"
                  value={companyForm.georeferencia}
                  onChange={(e) => setCompanyForm({ ...companyForm, georeferencia: e.target.value })}
                  className="form-input"
                  placeholder="4.8097, -75.6906"
                  disabled={updateLoading}
                />
              </div>

              {/* Gerencia Comercial */}
              <div style={{ gridColumn: '1 / -1', marginTop: '1rem' }}>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '1rem', color: 'var(--primary-red)' }}>Gerencia Comercial</h3>
              </div>

              <div className="form-group">
                <label className="form-label">Teléfono Gerente</label>
                <input
                  type="tel"
                  value={companyForm.tel_gerente}
                  onChange={(e) => setCompanyForm({ ...companyForm, tel_gerente: e.target.value })}
                  className="form-input"
                  disabled={updateLoading}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email Gerencia</label>
                <input
                  type="email"
                  value={companyForm.email_gerencia}
                  onChange={(e) => setCompanyForm({ ...companyForm, email_gerencia: e.target.value })}
                  className="form-input"
                  disabled={updateLoading}
                />
              </div>

              {/* Comercial */}
              <div style={{ gridColumn: '1 / -1', marginTop: '1rem' }}>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '1rem', color: 'var(--primary-red)' }}>Comercial</h3>
              </div>

              <div className="form-group">
                <label className="form-label">Teléfono Comercial</label>
                <input
                  type="tel"
                  value={companyForm.tel_comercial}
                  onChange={(e) => setCompanyForm({ ...companyForm, tel_comercial: e.target.value })}
                  className="form-input"
                  required
                  disabled={updateLoading}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email Comercial</label>
                <input
                  type="email"
                  value={companyForm.email_comercial}
                  onChange={(e) => setCompanyForm({ ...companyForm, email_comercial: e.target.value })}
                  className="form-input"
                  required
                  disabled={updateLoading}
                />
              </div>

              {/* Asesor Comercial */}
              <div style={{ gridColumn: '1 / -1', marginTop: '1rem' }}>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '1rem', color: 'var(--primary-red)' }}>Asesor Comercial</h3>
              </div>

              <div className="form-group">
                <label className="form-label">Teléfono Asesor</label>
                <input
                  type="tel"
                  value={companyForm.tel_asesor}
                  onChange={(e) => setCompanyForm({ ...companyForm, tel_asesor: e.target.value })}
                  className="form-input"
                  disabled={updateLoading}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email Asesor</label>
                <input
                  type="email"
                  value={companyForm.email_asesor}
                  onChange={(e) => setCompanyForm({ ...companyForm, email_asesor: e.target.value })}
                  className="form-input"
                  disabled={updateLoading}
                />
              </div>

              {/* Otros Departamentos */}
              <div style={{ gridColumn: '1 / -1', marginTop: '1rem' }}>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '1rem', color: 'var(--primary-red)' }}>Otros Departamentos</h3>
              </div>

              <div className="form-group">
                <label className="form-label">Email Servicio al Cliente</label>
                <input
                  type="email"
                  value={companyForm.email_servcliente}
                  onChange={(e) => setCompanyForm({ ...companyForm, email_servcliente: e.target.value })}
                  className="form-input"
                  disabled={updateLoading}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email Contabilidad</label>
                <input
                  type="email"
                  value={companyForm.email_contabilidad}
                  onChange={(e) => setCompanyForm({ ...companyForm, email_contabilidad: e.target.value })}
                  className="form-input"
                  disabled={updateLoading}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Teléfono Bodega</label>
                <input
                  type="tel"
                  value={companyForm.tel_bodega}
                  onChange={(e) => setCompanyForm({ ...companyForm, tel_bodega: e.target.value })}
                  className="form-input"
                  disabled={updateLoading}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email Bodega</label>
                <input
                  type="email"
                  value={companyForm.email_bodega}
                  onChange={(e) => setCompanyForm({ ...companyForm, email_bodega: e.target.value })}
                  className="form-input"
                  disabled={updateLoading}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email Dirección Técnica</label>
                <input
                  type="email"
                  value={companyForm.email_directecnica}
                  onChange={(e) => setCompanyForm({ ...companyForm, email_directecnica: e.target.value })}
                  className="form-input"
                  disabled={updateLoading}
                />
              </div>
            </div>

            <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem' }}>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={updateLoading}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                {updateLoading ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    Actualizando...
                  </>
                ) : (
                  'Guardar Cambios'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

/**
 * Product Detail Page component.
 * Displays detailed information about a specific product category (Medicamentos, Dispositivos, Insumos).
 * Includes a gallery, features list, and subcategories.
 * 
 * @param {Object} props
 * @param {string} props.category - The category to display ('medicamentos', 'dispositivos', 'insumos').
 * @param {Function} props.setCurrentPage - Function to navigate to other pages.
 */
const ProductDetailPage = ({ category, setCurrentPage }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const categoryInfo = {
    medicamentos: {
      title: 'Medicamentos',
      description: 'Distribuimos una amplia gama de medicamentos de alta calidad, respaldados por laboratorios reconocidos y con todos los registros sanitarios requeridos.',
      images: [
        { url: genericos, alt: 'Variedad de medicamentos', title: 'Amplia gama de medicamentos' },
        { url: comerciales, alt: 'Control de calidad', title: 'Control de calidad riguroso' },
        { url: altocosto, alt: 'Almacenamiento', title: 'Almacenamiento controlado' },
        { url: fabotera, alt: 'Distribución', title: 'Sistema de distribución eficiente' }
      ],
      features: [
        'Medicamentos genéricos y de marca',
        'Productos con registro INVIMA',
        'Control estricto de temperatura y humedad',
        'Trazabilidad completa de lotes',
        'Asesoría farmacéutica especializada',
        'Disponibilidad permanente'
      ],
      categories: [
        { name: 'Medicamentos Genéricos y Éticos', description: 'Comercializamos medicamentos esenciales bajo estándares de calidad y cumplimiento regulatorio, garantizando tratamientos seguros y accesibles. Analgésicos, antibióticos, antihipertensivos, antidiabéticos, entre otros.' },
        { name: 'Medicamentos Comerciales', description: 'Ofrecemos una amplia gama de medicamentos de marca reconocida para diversas especialidades médicas. Fórmulas comerciales aprobadas para uso institucional y privado.' },
        { name: 'Medicamentos de Alto Costo, UCI y Vacunas', description: 'Suministramos medicamentos especializados para unidades de cuidados intensivos, tratamientos de alto costo y programas de vacunación. Medicamentos oncológicos, inmunosupresores, biológicos y antivirales.' },
        { name: 'Faboterapéuticos', description: 'Ofrecemos soluciones terapéuticas complementarias para procesos de rehabilitación, tratamiento del dolor y soporte clínico. Terapias inhaladas, soluciones electrolíticas, fórmulas nutricionales y más.' }
      ]
    },
    dispositivos: {
      title: 'Equipos Médicos Menores',
      description: 'Ofrecemos equipos y dispositivos médicos de última tecnología, con garantía y soporte técnico especializado.',
      images: [
        { url: equipo1, alt: 'Equipos médicos avanzados', title: 'Tecnología de última generación' },
        { url: equipo2, alt: 'Mantenimiento de equipos', title: 'Servicio técnico especializado' },
        { url: equipo3, alt: 'Capacitación', title: 'Capacitación y soporte' },
        { url: equipo4, alt: 'Instalación', title: 'Instalación profesional' }
      ],
      features: [
        'Estetoscopios',
        'Balanzas',
        'Oxímetros',
        'Tensiómetros',
        'Mesas ginecológicas',
        'Garantía y servicio técnico',
        'Capacitación en uso y mantenimiento',
        'Asesoría en selección de equipos',
        'Soporte post-venta'
      ],
      categories: [
        { name: 'Equipos Menores', description: 'Estetoscopios, balanzas, oxímetros, lámparas, tensiómetros, mesas ginecológicas, etc.' },
        { name: 'Terapia Respiratoria', description: 'Equipos para tratamiento y soporte respiratorio' },
        { name: 'Equipos de Medición', description: 'Instrumentos precisos para signos vitales' },
        { name: 'Rehabilitación', description: 'Dispositivos para terapia física y rehabilitación' }
      ]
    },
    insumos: {
      title: 'Insumos Médicos',
      description: 'Distribuimos insumos y dispositivos que cumplen con la normativa sanitaria vigente, adaptados a todo tipo de instituciones médicas. Guantes, jeringas, cánulas, sondas, equipos de infusión, termómetros, tensiómetros, entre otros.',
      images: [
        { url: insumos1, alt: 'Insumos médicos', title: 'Variedad de insumos médicos' },
        { url: insumos2, alt: 'Material quirúrgico', title: 'Material quirúrgico especializado' },
        { url: insumos3, alt: 'Elementos de protección', title: 'Elementos de protección personal' },
        { url: insumos4, alt: 'Almacenamiento de insumos', title: 'Control de inventario' }
      ],
      features: [
        'Productos de alta calidad',
        'Stock permanente',
        'Precios competitivos',
        'Entrega inmediata',
        'Variedad de presentaciones',
        'Asesoría en selección de productos'
      ],
      categories: [
        { name: 'Material de Curación', description: 'Gasas, vendas, apósitos y material de curación' },
        { name: 'Ropa Hospitalaria', description: 'Ofrecemos ropa médica funcional y de alta calidad para personal de salud, pacientes e instituciones clínicas. Uniformes clínicos, pijamas, batas quirúrgicas, sabanas hospitalarias, cofias, tapabocas, etc.' },
        { name: 'Kits para Estéticas', description: 'Ofrecemos ropa médica funcional y de alta calidad para personal de salud, pacientes e instituciones clínicas. Guantes, gasas, agujas, mascarillas, productos para procedimientos estéticos.' },
        { name: 'Desechables', description: 'Insumos médicos de un solo uso' }
      ]
    }
  };

  const info = categoryInfo[category];

  return (
    <div className="container product-detail-page" style={{ paddingTop: '120px', paddingBottom: '4rem' }}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="product-detail-content"
      >
        <h1 className="section-title">{info.title}</h1>

        <div className="product-detail-section">
          <div className="product-hero">
            <p className="product-detail-description" style={{ fontSize: '1.2rem', textAlign: 'center', maxWidth: '800px', margin: '0 auto 3rem', color: 'var(--text-secondary)' }}>
              {info.description}
            </p>

            {/* Galería de Imágenes */}
            <div className="product-gallery">
              <div className="gallery-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
                {info.images.map((image, index) => (
                  <motion.div
                    key={index}
                    className="gallery-item"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.03 }}
                    onClick={() => setSelectedImage(image)}
                    style={{ cursor: 'pointer', borderRadius: '1rem', overflow: 'hidden', boxShadow: 'var(--shadow-md)' }}
                  >
                    <div className="gallery-image-container" style={{ height: '200px' }}>
                      <img
                        src={image.url}
                        alt={image.alt}
                        className="gallery-image"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Modal de imagen ampliada */}
            <AnimatePresence>
              {selectedImage && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="image-modal-overlay"
                  onClick={() => setSelectedImage(null)}
                  style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.9)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}
                >
                  <motion.div
                    className="image-modal-content"
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0.8 }}
                    onClick={(e) => e.stopPropagation()}
                    style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}
                  >
                    <img
                      src={selectedImage.url}
                      alt={selectedImage.alt}
                      className="modal-image"
                      style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: '0.5rem' }}
                    />
                    <button
                      className="modal-close-btn"
                      onClick={() => setSelectedImage(null)}
                      style={{ position: 'absolute', top: '-40px', right: 0, color: 'white', background: 'none', border: 'none', fontSize: '2rem', cursor: 'pointer' }}
                    >
                      ✕
                    </button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="features-section" style={{ marginTop: '4rem' }}>
            <h2 className="subsection-title" style={{ fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center' }}>Características Principales</h2>
            <div className="features-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
              {info.features.map((feature, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -5 }}
                  className="feature-card glass"
                  style={{ padding: '1.5rem', borderRadius: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}
                >
                  <div className="feature-icon" style={{ color: 'var(--primary-red)', fontWeight: 'bold' }}>✓</div>
                  <p className="feature-text" style={{ margin: 0 }}>{feature}</p>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="categories-section" style={{ marginTop: '4rem' }}>
            <h2 className="subsection-title" style={{ fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center' }}>Categorías</h2>
            <div className="categories-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
              {info.categories.map((cat, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.02 }}
                  className="category-detail-card glass"
                  style={{ padding: '1.5rem', borderRadius: '1rem', borderLeft: '4px solid var(--primary-red)' }}
                >
                  <h3 className="category-detail-title" style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{cat.name}</h3>
                  <p className="category-detail-description" style={{ color: 'var(--text-secondary)' }}>{cat.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="contact-section" style={{ marginTop: '4rem', textAlign: 'center', background: 'var(--bg-accent)', padding: '3rem', borderRadius: '1rem' }}>
            <h2 className="subsection-title" style={{ fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '1rem' }}>¿Necesita más información?</h2>
            <p className="contact-description" style={{ maxWidth: '600px', margin: '0 auto 2rem', color: 'var(--text-secondary)' }}>
              Nuestro equipo de asesores especializados está disponible para resolver
              todas sus dudas y ayudarle a encontrar los productos que mejor se adapten
              a sus necesidades.
            </p>
            <div className="contact-actions" style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => setCurrentPage('quote')}
                className="btn btn-primary"
              >
                Solicitar Cotización
              </button>
              <button
                onClick={() => setCurrentPage('contact')}
                className="btn btn-secondary"
              >
                Contactar un Asesor
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

/**
 * Login Page component.
 * Displays the login form for accessing the admin panel.
 * 
 * @param {Object} props
 * @param {Function} props.handleLogin - Function to handle form submission.
 * @param {Object} props.loginForm - State object for email and password.
 * @param {Function} props.setLoginForm - Function to update login form state.
 * @param {string} props.loginError - Error message to display if login fails.
 * @param {boolean} props.loginLoading - Loading state for the login request.
 */
const LoginPage = ({ handleLogin, loginForm, setLoginForm, loginError, loginLoading }) => (
  <div className="login-page" style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-secondary)' }}>
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="login-card glass"
      style={{ padding: '3rem', borderRadius: '1rem', width: '100%', maxWidth: '400px' }}
    >
      <div className="login-header" style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h1 className="login-title" style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary-red)' }}>Dismhecol</h1>
        <p className="login-subtitle" style={{ color: 'var(--text-secondary)' }}>Panel de Administración</p>
      </div>

      {loginError && (
        <div className="login-error" style={{
          padding: '0.75rem',
          marginBottom: '1rem',
          background: '#fee2e2',
          color: '#991b1b',
          borderRadius: '0.5rem',
          fontSize: '0.9rem',
          textAlign: 'center'
        }}>
          {loginError}
        </div>
      )}

      <form onSubmit={handleLogin} className="login-form">
        <div className="form-group">
          <label className="form-label">Correo Electrónico</label>
          <input
            type="email"
            value={loginForm.email}
            onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
            className="form-input"
            placeholder="Ingrese su correo electrónico"
            required
            disabled={loginLoading}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Contraseña</label>
          <input
            type="password"
            value={loginForm.password}
            onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
            className="form-input"
            placeholder="Ingrese su contraseña"
            required
            disabled={loginLoading}
          />
        </div>

        <button
          type="submit"
          className="btn btn-primary login-submit-btn"
          style={{ width: '100%', marginTop: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
          disabled={loginLoading}
        >
          {loginLoading ? (
            <>
              <Loader className="animate-spin" size={20} />
              Iniciando sesión...
            </>
          ) : (
            'Iniciar Sesión'
          )}
        </button>
      </form>
    </motion.div>
  </div>
);

/**
 * Main App Component.
 * Handles routing, state management, and overall layout of the application.
 * Manages global state like current page, user authentication, and theme.
 */
const App = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('medicamentos');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [adminView, setAdminView] = useState(false);
  const [contactInfo, setContactInfo] = useState(mockContactInfo);
  const [scrolled, setScrolled] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    // Check localStorage for saved theme preference
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark';
  });

  // Apply theme to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.setAttribute('data-theme', 'dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  // Toggle theme function
  const toggleTheme = () => {
    setDarkMode(!darkMode);
  };

  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      const decoded = decodeJWT(token);
      if (decoded) {
        setUserData(decoded);
        setIsLoggedIn(true);
      } else {
        // Invalid token, clear it
        localStorage.removeItem('authToken');
      }
    }
  }, []);

  // Set initial page based on URL path
  useEffect(() => {
    const path = window.location.pathname.substring(1); // Remove leading slash
    if (path && ['home', 'about', 'contact', 'quote', 'privacy', 'policies', 'cum-ium', 'admin', 'medicamentos', 'dispositivos', 'insumos'].includes(path)) {
      setCurrentPage(path);
    }
  }, []);

  // Update URL when currentPage changes
  useEffect(() => {
    const path = currentPage === 'home' ? '/' : `/${currentPage}`;
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
    }
  }, [currentPage]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [currentPage]);

  // Fetch company information on mount
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/data/informacion`);

        if (!response.ok) {
          throw new Error('Error al obtener información de la empresa');
        }

        const data = await response.json();

        if (data.ok && Array.isArray(data.data) && data.data.length > 0) {
          const info = data.data[0];

          // Transform API data to match contactInfo structure
          const transformedInfo = {
            address: info.direccion || mockContactInfo.address,
            empresa: info.empresa || 'DISMHECOL SAS',
            tel_fijo: info.tel_fijo,
            departments: [
              {
                name: 'Gerencia Comercial',
                phone: info.tel_gerente,
                email: info.email_gerencia
              },
              {
                name: 'Comercial',
                phone: info.tel_comercial,
                email: info.email_comercial
              },
              {
                name: 'Asesor Comercial',
                phone: info.tel_asesor,
                email: info.email_asesor
              },
              {
                name: 'Servicio al Cliente',
                phone: null,
                email: info.email_servcliente
              },
              {
                name: 'Contabilidad',
                phone: null,
                email: info.email_contabilidad
              },
              {
                name: 'Bodega',
                phone: info.tel_bodega,
                email: info.email_bodega
              },
              {
                name: 'Dirección Técnica',
                phone: null,
                email: info.email_directecnica
              }
            ].filter(dept => dept.phone || dept.email), // Only include departments with contact info
            coordinates: info.georeferencia ? {
              lat: parseFloat(info.georeferencia.split(',')[0].trim()),
              lng: parseFloat(info.georeferencia.split(',')[1].trim())
            } : null,
            social: mockContactInfo.social // Keep mock social data
          };

          setContactInfo(transformedInfo);
        }
      } catch (err) {
        console.error('Error fetching company info:', err);
        // Keep using mockContactInfo if fetch fails
      }
    };

    fetchCompanyInfo();
  }, []);

  // Handle scroll for sticky header
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Carousel auto-rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % mockCarouselImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: loginForm.email,
          password: loginForm.password
        })
      });

      const data = await response.json();

      if (data.ok && data.data && data.data.token) {
        // Store token in localStorage
        localStorage.setItem('authToken', data.data.token);

        // Decode token to get user data
        const decoded = decodeJWT(data.data.token);
        if (decoded) {
          console.log('Decoded JWT payload:', decoded);
          setUserData(decoded);
        }

        setIsLoggedIn(true);
        setAdminView(true);
        setLoginForm({ email: '', password: '' });
      } else {
        setLoginError(data.message || 'Credenciales inválidas.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Error al conectar con el servidor. Por favor, intente nuevamente.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    // Clear token from localStorage
    localStorage.removeItem('authToken');
    setIsLoggedIn(false);
    setAdminView(false);
    setUserData(null);
    setLoginForm({ email: '', password: '' });
    setCurrentPage('home');
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'home': return <HomePage currentSlide={currentSlide} setCurrentSlide={setCurrentSlide} setCurrentPage={setCurrentPage} contactInfo={contactInfo} />;
      case 'medicamentos': return <ProductDetailPage category="medicamentos" setCurrentPage={setCurrentPage} />;
      case 'dispositivos': return <ProductDetailPage category="dispositivos" setCurrentPage={setCurrentPage} />;
      case 'insumos': return <ProductDetailPage category="insumos" setCurrentPage={setCurrentPage} />;
      case 'about': return <AboutPage />;
      case 'contact': return <ContactPage contactInfo={contactInfo} />;
      case 'quote': return <QuotePage />;
      case 'pqrs': return <PQRSPage />;
      case 'privacy': return <PrivacyPage />;
      case 'policies': return <PoliciesPage />;
      case 'cum-ium': return <CumIumSearchPage />;
      case 'admin': return <AdminPanel handleLogout={handleLogout} contactInfo={contactInfo} setContactInfo={setContactInfo} userData={userData} />;
      default: return <HomePage currentSlide={currentSlide} setCurrentSlide={setCurrentSlide} setCurrentPage={setCurrentPage} contactInfo={contactInfo} />;
    }
  };

  if (currentPage === 'admin' && !isLoggedIn) {
    return <LoginPage handleLogin={handleLogin} loginForm={loginForm} setLoginForm={setLoginForm} loginError={loginError} loginLoading={loginLoading} />;
  }

  return (
    <div className="app">
      {/* Header */}
      <header className={`header ${scrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="navbar">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="logo-container"
              onClick={() => setCurrentPage('home')}
              style={{ cursor: 'pointer' }}
            >
              <img
                src={logo}
                alt="Dismhecol Logo"
                className="logo-image"
                style={{ height: '50px', width: 'auto' }}
              />
            </motion.div>

            {/* Desktop Navigation */}
            <nav className="nav-links">
              {[
                { id: 'home', label: 'Inicio' },
                { id: 'about', label: 'Nosotros' },
                { id: 'cum-ium', label: 'Consulta CUM/IUM' },
                { id: 'policies', label: 'Políticas' },
                { id: 'quote', label: 'Cotización' },
                { id: 'pqrs', label: 'PQRS' },
                { id: 'contact', label: 'Contacto' },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => setCurrentPage(item.id)}
                  className={`nav-link ${currentPage === item.id ? 'active' : ''}`}
                >
                  {item.label}
                </button>
              ))}

              {isLoggedIn && (
                <button
                  onClick={() => setCurrentPage('admin')}
                  className="btn btn-primary"
                  style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                >
                  Admin
                </button>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mobile-menu"
            >
              <div className="container">
                <div className="mobile-menu-content">
                  {[
                    { id: 'home', label: 'Inicio' },
                    { id: 'about', label: 'Nosotros' },
                    { id: 'cum-ium', label: 'Consulta CUM/IUM' },
                    { id: 'policies', label: 'Políticas' },
                    { id: 'quote', label: 'Cotización' },
                    { id: 'pqrs', label: 'PQRS' },

                    { id: 'contact', label: 'Contacto' },
                  ].map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        setCurrentPage(item.id);
                        setMobileMenuOpen(false);
                      }}
                      className={`mobile-menu-item ${currentPage === item.id ? 'active' : ''}`}
                    >
                      {item.label}
                    </button>
                  ))}

                  {isLoggedIn && (
                    <button
                      onClick={() => {
                        setCurrentPage('admin');
                        setMobileMenuOpen(false);
                      }}
                      className="mobile-menu-item"
                    >
                      Panel de Administración
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="main-content"
          >
            {renderPage()}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-column">
              <div className="footer-logo" style={{ marginBottom: '1rem' }}>
                <img
                  src={logo}
                  alt="Dismhecol Logo"
                  style={{ height: '40px', width: 'auto' }}
                />
              </div>
              <p className="footer-description">
                Su proveedor confiable de medicamentos, dispositivos e insumos médicos.
              </p>
              <div className="social-links" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <a href="https://www.facebook.com/share/17nRm1r3Kc/" className="social-link">
                  <Facebook size={18} />
                </a>
                <a href="https://www.instagram.com/dismhecolsas/" className="social-link">
                  <Instagram size={18} />
                </a>
                <a href="https://x.com/dismhecol?t=P3-Pa05qGd9D4Rdp8nLCsg&s=08" className="social-link">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
                <a href="#" className="social-link">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                  </svg>
                </a>
              </div>
            </div>

            <div className="footer-column">
              <h4 className="footer-heading">Productos</h4>
              <ul className="footer-links">
                <li><button onClick={() => setCurrentPage('medicamentos')} className="footer-link">Medicamentos</button></li>
                <li><button onClick={() => setCurrentPage('dispositivos')} className="footer-link">Equipos Médicos menores</button></li>
                <li><button onClick={() => setCurrentPage('insumos')} className="footer-link">Insumos Médicos</button></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4 className="footer-heading">Empresa</h4>
              <ul className="footer-links">
                <li><button onClick={() => setCurrentPage('about')} className="footer-link">Nosotros</button></li>
                <li><button onClick={() => setCurrentPage('policies')} className="footer-link">Políticas</button></li>
                <li><button onClick={() => setCurrentPage('quote')} className="footer-link">Cotización</button></li>
                <li><button onClick={() => setCurrentPage('pqrs')} className="footer-link">PQRS</button></li>
                <li><button onClick={() => setCurrentPage('contact')} className="footer-link">Contacto</button></li>
                <li><button onClick={() => setCurrentPage('privacy')} className="footer-link">Privacidad</button></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4 className="footer-heading">Contacto</h4>
              <div className="contact-details">
                <div className="contact-item">
                  <MapPin size={18} className="contact-icon" />
                  <span>{contactInfo.address}</span>
                </div>
                {contactInfo.tel_fijo && (
                  <div className="contact-item">
                    <Phone size={18} className="contact-icon" />
                    <span>{contactInfo.tel_fijo}</span>
                  </div>
                )}
                {contactInfo.departments && contactInfo.departments.length > 0 && contactInfo.departments[0].email && (
                  <div className="contact-item">
                    <Mail size={18} className="contact-icon" />
                    <span>{contactInfo.departments[0].email}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="footer-divider">
            <p>&copy; 2025 Dismhecol. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Float Button - Fixed Position */}
      {currentPage !== 'admin' && currentPage !== 'login' && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          whileHover={{ scale: 1.1 }}
          transition={{ delay: 1 }}
          className="whatsapp-float"
          onClick={() => window.open('https://wa.me/+573174321115', '_blank')}
        >
          <div className="whatsapp-icon" style={{ width: '24px', height: '24px' }}>
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
          </div>
          <span className="whatsapp-text">Escríbenos</span>
        </motion.button>
      )}

      {/* Theme Toggle Button - Fixed Position */}
      {currentPage !== 'admin' && currentPage !== 'login' && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          transition={{ delay: 1.2 }}
          className="theme-toggle"
          onClick={toggleTheme}
          aria-label="Toggle theme"
        >
          {darkMode ? <Sun size={24} /> : <Moon size={24} />}
        </motion.button>
      )}
    </div>
  );
};

export default App;